import {useState} from 'react'
import {css} from '@emotion/react'
import {Button, List} from 'antd'
import http from '@/biz/http.mjs'
import {useMapContext} from '@/components/MindMap'
import {Modal, useOpenModal} from '@/components/Modal'
import {TextArea} from '@/components/Form'
import runAsync from '@/script/runAsync.mjs'
import useSwr from '@/script/useSwrNoCache.mjs'
import {useProperties} from '../../SidePanel/RightPanel/Properties.mjs'
import LongText from '../../SidePanel/RightPanel/PropertiesControllerLongText.jsx'

const cssKnowledges = css({
    display: 'grid',
    gridTemplateColumns: '160px 1fr',
    gap: 8,
    overflow: 'hidden',
})

const useKnowledges = ({bdCode, prjId, ruleCode, sVer}) => {
    return useSwr(
        () => runAsync(
            () => ruleCode ?
                http.get('/dmbds/ruleRkList', {bdCode, ruleCode, prjId, sVer})
                :
                http.get('/dmbds/bdRkList', {bdCode, prjId, sVer})
            ,
            {action: '查询逆向建模规则列表'}
        ),

        {dependencies: [bdCode, prjId, ruleCode, sVer]}
    )
}

const ModalDomainKnowledge = ({
    bdCode,
    prjId,
    readOnly,
    ruleCode,
    sVer,
    onOk,
    ...props
}) => {
    const [current, setCurrent] = useState(0)
    const {data, mutate} = useKnowledges({bdCode, prjId, ruleCode, sVer})

    const dataSource = (() => {
        if (! data) {
            return []
        }
        else if (1 < data.length) {
            return data.slice(0, -1).map((e, index) => ({...e, index}))
        }
        else {
            return data.map((e, index) => ({...e, index}))
        }
    })()

    const list = (
        <List
            className="overflow-auto"
            dataSource={dataSource}
            itemLayout="horizontal"

            renderItem={
                e => (
                    <List.Item
                        className={e.index === current ? 'bg-[#e6f7ff]' : ''}
                        onClick={() => setCurrent(e.index)}
                    >
                        <List.Item.Meta
                            title={e.uptName}
                            description={e.uptTime}
                        />
                    </List.Item>
                )
            }

            rowKey="pkid"
        />
    )

    const onChange = revKnowledge => mutate(
        data => data.with(0, {...data[0], revKnowledge}),
        {revalidate: false}
    )

    const _onOk = async () => {
        const {pkid, revKnowledge} = data[0]

        await runAsync(
            () => http.post(
                '/dmbds/modifyRk',
                {pkid, revKnowledge}
            ),

            {
                action: '更新逆向建模规则',
                success: {show: true},
            }
        )

        onOk(true)
    }

    return (
        <Modal
            title="逆向建模规则"
            width={800}
            height={600}
            okText="保存"
            onOk={_onOk}
            {...props}
        >
            <div css={cssKnowledges}>
                {list}

                <TextArea
                    className="!h-full"
                    readOnly={0 < current}
                    value={data?.[current]?.revKnowledge}
                    onChange={onChange}
                />
            </div>
        </Modal>
    )
}

const ButtonManage = ({
    bdCode,
    items,
    prjId,
    readOnly,
    ruleCode,
    sVer,
    onChange,
    ...props
}) => {
    const openModal = useOpenModal()

    if (! items) {
        return null
    }

    const onClick = async () => {
        if (0 === items.length) {
            await runAsync(
                () => {
                    if (ruleCode) {
                        return http.post(
                            '/dmbds/addLogicRk',
                            {bdCode, ruleCode, prjId, revKnowledge: '', sVer}
                        )
                    }
                    else {
                        return http.post(
                            '/dmbds/addBdRk',
                            {bdCode, prjId, revKnowledge: '', sVer}
                        )
                    }
                },

                {action: '初始化逆向建模规则'}
            )
        }

        const isModified = await openModal(
            <ModalDomainKnowledge
                bdCode={bdCode}
                prjId={prjId}
                readOnly={readOnly}
                ruleCode={ruleCode}
                sVer={sVer}
            />
        )

        if (isModified) {
            onChange()
        }
    }

    return (
        <Button
            size="small"
            type="link"
            onClick={onClick}
            {...props}
        >
            管理
        </Button>
    )
}

const DomainKnowledge = ({bdCode, prjId, ruleCode, sVer, ...props}) => {
    const map = useMapContext()
    const permission = map.usePermission()
    const {data, mutate} = useKnowledges({bdCode, prjId, ruleCode, sVer})
    const value = 0 < data?.length ?  data[0].revKnowledge : ''

    const buttonManage = (
        <ButtonManage
            bdCode={bdCode}
            items={data}
            prjId={prjId}
            readOnly={! permission.has('write')}
            ruleCode={ruleCode}
            sVer={sVer}
            onChange={mutate}
        />
    )

    return (
        <LongText
            label="逆向建模规则"
            labelSuffix={buttonManage}
            readOnly
            value={value}
            {...props}
        />
    )
}

export default function PropertiesPaneControllerDomainKnowledge(props) {
    const [{bdCode, prjId, ruleCode, sVer}] = useProperties()

    if (! bdCode) {
        return null
    }

    return (
        <DomainKnowledge
            bdCode={bdCode}
            prjId={prjId}
            ruleCode={ruleCode}
            sVer={sVer}
            {...props}
        />
    )
}
